import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { SettingDto, SettingClientService } from '@api-client';

/**
 * 회사 정보 인터페이스
 */
export interface CompanyInfo {
  /** 회사명 */
  name: string;
  /** 주소 */
  address: string;
  addressDetail: string;
  fullAddress: string;
  /** 전화번호 */
  tel: string;
  /** 이메일 */
  email: string;
  copyright: string;
  /** 사업자 등록번호 */
  registrationNumber: string;
  /** 대표자명 */
  ceo: string;
  /** 로고 URL */
  logo: string;
}

/**
 * 설정값 스토어
 * 앱 전역에서 사용되는 사이트 정보 및 회사 설정값을 관리합니다.
 */
@Injectable({ providedIn: 'root' })
export class SettingStore {
  private readonly settingService = inject(SettingClientService);
  private readonly platformId = inject(PLATFORM_ID);

  /** 설정값 로딩 상태 */
  readonly isLoading = signal<boolean>(false);

  /** 설정값 로딩 완료 여부 */
  readonly isLoaded = signal<boolean>(false);

  /** 원본 설정값 목록 */
  readonly settings = signal<SettingDto[]>([]);

  /** 회사 정보 */
  readonly companyInfo = signal<CompanyInfo>({
    name: '',
    address: '',
    addressDetail: '',
    fullAddress: '',
    tel: '',
    email: '',
    copyright: '',
    registrationNumber: '',
    ceo: '',
    logo: '',
  });

  constructor() {
    this.fetch();
  }

  /**
   * 설정값 조회
   */
  fetch(): void {
    if (isPlatformServer(this.platformId)) return;
    if (this.isLoaded() || this.isLoading()) return;

    this.isLoading.set(true);

    this.settingService.settingClientControllerGetSettingsV1().subscribe({
      next: settings => {
        this.settings.set(settings);
        this.parseCompanyInfo(settings);
        this.isLoaded.set(true);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  /**
   * 특정 키의 설정값 조회
   * @param key 설정 키
   * @returns 설정값 또는 빈 문자열
   */
  getValue(key: string): string {
    return this.settings().find(s => s.key === key)?.value || '';
  }

  /**
   * 회사 정보 파싱
   * @param settings 설정값 목록
   */
  private parseCompanyInfo(settings: SettingDto[]): void {
    const getSettingValue = (key: string): string =>
      settings.find(setting => setting.key === key)?.value || '';
    const address = getSettingValue('COMPANY:ADDRESS');
    const addressDetail = getSettingValue('COMPANY:ADDRESS_DETAIL');

    const info: CompanyInfo = {
      name: getSettingValue('COMPANY:NAME'),
      address,
      addressDetail,
      fullAddress: [address, addressDetail].filter(Boolean).join(' '),
      tel: getSettingValue('COMPANY:TEL'),
      email: getSettingValue('COMPANY:EMAIL'),
      copyright: getSettingValue('COMPANY:COPYRIGHT'),
      registrationNumber: getSettingValue('COMPANY:REGISTRATION_NUMBER'),
      ceo: getSettingValue('COMPANY:CEO'),
      logo: getSettingValue('COMPANY:LOGO'),
    };
    this.companyInfo.set(info);
  }
}
